import { DoneAll, WarningAmber } from "@mui/icons-material";
import { Chip, Tooltip } from "@mui/material";
import { TemplateConfig } from "@types";
import MarkdownPreview from "@uiw/react-markdown-preview";
import React from "react";
import { getDocConfigByElementId } from "src/utils/documents";

type Props = {
  dataKey: string;
  value: {
    complete: boolean;
    value: string | undefined;
  };
  handleDocumentClick: (templateKey: string) => void;
  templatesConfig: TemplateConfig;
};

export const ContextChip: React.FC<Props> = ({
  dataKey: key,
  value,
  handleDocumentClick,
  templatesConfig,
}) => {
  const handleClick = () => {
    const docConfig = getDocConfigByElementId(key, templatesConfig);
    handleDocumentClick(docConfig.templateKey);
  };

  const handleLinkClick = (event: React.MouseEvent<HTMLDivElement>) => {
    const clickedElement = event.target as HTMLElement;
    const isLink = clickedElement.tagName === "A";

    if (isLink) {
      event.preventDefault();
      handleClick();
    }
  };

  const chipIcon = value.complete ? (
    <DoneAll fontSize="small" color="success" />
  ) : (
    <WarningAmber fontSize="small" color="error" />
  );

  return (
    <Tooltip
      key={key}
      title={
        <div onClick={handleLinkClick}>
          <MarkdownPreview
            source={
              value.value
                ? `${value.value}`
                : `This document has limited context because the ${key} data key is missing. <u>[Click here to complete the document](#)</u>`
            }
            wrapperElement={{ "data-color-mode": "dark" }}
            style={{
              backgroundColor: "transparent",
              fontSize: "0.75rem",
              margin: 0,
              maxHeight: "300px",
              overflow: "auto",
            }}
            className="bg-transparent"
          />
        </div>
      }
      placement="top"
      componentsProps={{
        tooltip: {
          sx: {
            "& .wmde-markdown": {
              backgroundColor: "transparent !important",
              fontSize: "0.75rem !important",
              color: "inherit !important",
              lineHeight: "1.3 !important",
            },
            "& .wmde-markdown *": {
              backgroundColor: "transparent !important",
              fontSize: "0.75rem !important",
              color: "inherit !important",
            },
            "& .wmde-markdown p": {
              lineHeight: "1.3 !important",
              margin: "0.25em 0 !important",
            },
            "& .wmde-markdown p:first-of-type": {
              marginTop: "0 !important",
            },
            "& .wmde-markdown p:last-of-type": {
              marginBottom: "0 !important",
            },
            "& .wmde-markdown ul, & .wmde-markdown ol": {
              lineHeight: "1.3 !important",
              margin: "0.25em 0 !important",
              paddingLeft: "1em !important",
            },
            "& .wmde-markdown li": {
              lineHeight: "1.3 !important",
              margin: "0.1em 0 !important",
            },
            "& .wmde-markdown pre": {
              backgroundColor: "rgba(255, 255, 255, 0.1) !important",
              lineHeight: "1.2 !important",
              margin: "0.25em 0 !important",
              padding: "0.5em !important",
            },
            "& .wmde-markdown code": {
              backgroundColor: "rgba(255, 255, 255, 0.1) !important",
              lineHeight: "1.2 !important",
            },
            "& .wmde-markdown a": {
              textDecoration: "underline !important",
              color: "inherit !important",
            },
          },
        },
      }}
    >
      <Chip
        label={key.replace(/-/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}
        variant="outlined"
        size="tiny"
        color={"default"}
        icon={chipIcon}
        sx={{
          cursor: "pointer",
          width: "fit-content",
          "&:hover": {
            border: `1px solid ${value.complete ? "#2e7d32" : "#d32f2f"}`,
          },
        }}
      />
    </Tooltip>
  );
};
